import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useTags } from "@/context/TagsContext";
import { TagIcon, tagChipStyle } from "@/lib/tagStyles";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { SealBadge, Price } from "@/components/common";
import SignupDialog from "@/components/SignupDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Share2, Sparkles, Flower2, Info } from "lucide-react";

const FALLBACK = "data:image/svg+xml;utf8," + encodeURIComponent(
  `<svg xmlns='http://www.w3.org/2000/svg' width='800' height='600'><rect width='100%' height='100%' fill='hsl(36,28%,90%)'/><text x='50%' y='50%' font-size='42' fill='hsl(38,52%,46%)' text-anchor='middle' font-family='serif'>功德</text></svg>`
);

export default function MeritCard({ project }) {
  const { t, lang, pick } = useI18n();
  const { user } = useAuth();
  const { getTag } = useTags();
  const tagDef = getTag(project.category, project.subcategory);
  const navigate = useNavigate();
  const [openSignup, setOpenSignup] = useState(false);
  const [openDetail, setOpenDetail] = useState(false);

  const title = pick(project, "title") || project.title_zh;
  const desc = pick(project, "desc");
  const isFree = Number(project.price) === 0;
  const isShop = project.category === "shop";
  const stockTracked = isShop && (project.stock !== null && project.stock !== undefined);
  const soldOut = stockTracked && Number(project.stock) <= 0;

  // five-element tags (primary + extras, deduped)
  const elements = Array.from(new Set([project.element, ...(project.elements || [])].filter(Boolean)));
  const matchEls = project.match_elements || [];
  const ELEMENT_SWATCH = { 金: "#c9b273", 木: "#4f8a5b", 水: "#3f6ea5", 火: "#c0453b", 土: "#c79a4a" };
  const ElementBadges = ({ testidPrefix }) => (
    elements.length > 0 ? (
      <div className="flex flex-wrap items-center gap-1.5" data-testid={`${testidPrefix}-${project.id}`}>
        <span className="text-[11px] text-muted-foreground">{lang === "zh" ? "五行" : "Element"}</span>
        {elements.map((e) => {
          const hit = matchEls.includes(e);
          return (
            <Badge
              key={e}
              variant="outline"
              className="h-5 px-1.5 text-[11px] font-semibold"
              style={{ borderColor: ELEMENT_SWATCH[e], color: ELEMENT_SWATCH[e], background: hit ? `${ELEMENT_SWATCH[e]}1a` : "transparent" }}
            >
              {e}{hit ? " ✓" : ""}
            </Badge>
          );
        })}
      </div>
    ) : null
  );

  const CAT_PATH = { charity: "charity", lotus: "lotus", daily_merit: "daily", shop: "shop" };
  const handleShare = async () => {
    const ref = user?.referral_code ? `?ref=${user.referral_code}` : "";
    const url = `${window.location.origin}/${CAT_PATH[project.category] || "projects"}${ref}`;
    const shareData = { title: `${title} · 你好功德 CiAO Merit`, text: desc, url };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        toast.success(lang === "zh" ? "链接已复制，快去分享吧！" : "Link copied, share it!");
      }
    } catch (e) { /* user cancelled */ }
  };

  const handleSignupClick = () => {
    if (soldOut) return;
    if (!user) {
      toast.info(t("bankin.profileNeeded"));
      navigate("/member");
      return;
    }
    setOpenSignup(true);
  };

  return (
    <Card className="overflow-hidden card-lift border-border bg-card flex flex-col" data-testid={`merit-card-${project.id}`}>
      <div className="relative">
        <AspectRatio ratio={4 / 3}>
          <img
            src={project.image_url || FALLBACK}
            onError={(e) => { e.currentTarget.src = FALLBACK; }}
            alt={title}
            className="h-full w-full object-cover"
          />
        </AspectRatio>
        {project.promoted && (
          <SealBadge className="absolute top-3 left-3">
            <Sparkles className="h-3 w-3" /> {t("common.recommended")}
          </SealBadge>
        )}
        {project.category === "lotus" && (
          <Badge className="absolute top-3 right-3 bg-[hsl(var(--brand-gold-soft))] text-[hsl(var(--brand-ink))]">
            <Flower2 className="h-3 w-3 mr-1" /> {t("categories.lotus")}
          </Badge>
        )}
        {stockTracked && (
          <Badge
            className={`absolute top-3 right-3 ${soldOut ? "bg-[hsl(var(--brand-ink))]/70 text-white" : "bg-[hsl(var(--brand-gold-soft))] text-[hsl(var(--brand-ink))]"}`}
            data-testid={`shop-stock-${project.id}`}
          >
            {soldOut ? t("shop.soldOut") : `${t("shop.stock")} ${project.stock}`}
          </Badge>
        )}
      </div>
      <CardContent className="p-4 flex flex-col flex-1">
        {project.subcategory && (
          <Badge
            variant="outline"
            className="self-start mb-1.5 gap-1"
            style={tagChipStyle(tagDef?.color, false)}
            data-testid={`merit-tag-${project.id}`}
          >
            {tagDef?.icon && <TagIcon name={tagDef.icon} />}
            {project.subcategory}
          </Badge>
        )}
        <h3 className="font-display text-lg font-semibold leading-snug cursor-pointer hover:text-[hsl(var(--brand-bronze))]" onClick={() => setOpenDetail(true)} data-testid={`merit-title-${project.id}`}>{title}</h3>
        {desc && <p className="mt-1 text-sm text-muted-foreground leading-6 line-clamp-3">{desc}</p>}
        {elements.length > 0 && <div className="mt-1.5"><ElementBadges testidPrefix="merit-elements" /></div>}
        <button onClick={() => setOpenDetail(true)} className="self-start mt-1 inline-flex items-center gap-1 text-xs font-medium text-[hsl(var(--brand-bronze))] hover:underline" data-testid={`merit-detail-${project.id}`}>
          <Info className="h-3.5 w-3.5" />{lang === "zh" ? "查看详情" : "View details"}
        </button>
        <div className="mt-3 flex items-end justify-between">
          <div>
            {isFree ? (
              <span className="text-lg font-semibold text-[hsl(var(--brand-bronze))]">{t("common.free")}</span>
            ) : (
              <Price value={project.price} className="text-lg text-[hsl(var(--brand-ink))]" />
            )}
            {!isFree && (
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {t("common.earnPoints")} +{(Number(project.price) * 0.01).toFixed(2)} {t("common.pointsUnit")}
              </div>
            )}
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <Button
            className="flex-1 bg-[hsl(var(--primary))] text-primary-foreground"
            data-testid={`project-card-signup-button-${project.id}`}
            onClick={handleSignupClick}
            disabled={soldOut}
          >
            {isShop ? (soldOut ? t("shop.soldOut") : t("shop.buy")) : t("common.signup")}
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="border-[hsl(var(--brand-gold))]"
            data-testid={`project-card-share-button-${project.id}`}
            onClick={handleShare}
            aria-label={t("common.share")}
          >
            <Share2 className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
      {openSignup && (
        <SignupDialog project={project} open={openSignup} onOpenChange={setOpenSignup} />
      )}
      <Dialog open={openDetail} onOpenChange={setOpenDetail}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid={`merit-detail-dialog-${project.id}`}>
          <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <AspectRatio ratio={4 / 3}>
              <img src={project.image_url || FALLBACK} onError={(e) => { e.currentTarget.src = FALLBACK; }} alt={title} className="h-full w-full object-cover rounded-lg" />
            </AspectRatio>
            <div className="flex flex-wrap items-center gap-2">
              {isFree ? (
                <span className="text-lg font-semibold text-[hsl(var(--brand-bronze))]">{t("common.free")}</span>
              ) : (
                <Price value={project.price} className="text-xl text-[hsl(var(--brand-ink))]" />
              )}
              {!isFree && <Badge variant="outline" className="border-[hsl(var(--brand-gold))] text-[hsl(var(--brand-bronze))]">{t("common.earnPoints")} +{(Number(project.price) * 0.01).toFixed(2)} {t("common.pointsUnit")}</Badge>}
              {project.subcategory && <Badge variant="outline" style={tagChipStyle(tagDef?.color, false)}>{project.subcategory}</Badge>}
              {stockTracked && <Badge className={soldOut ? "bg-[hsl(var(--brand-ink))]/70 text-white" : "bg-[hsl(var(--brand-gold-soft))] text-[hsl(var(--brand-ink))]"}>{soldOut ? t("shop.soldOut") : `${t("shop.stock")} ${project.stock}`}</Badge>}
            </div>
            {elements.length > 0 && <ElementBadges testidPrefix="merit-detail-elements" />}
            {desc && <p className="text-sm text-foreground/85 leading-7 whitespace-pre-wrap">{desc}</p>}
            <div className="flex gap-2 pt-1">
              <Button className="flex-1 bg-[hsl(var(--primary))] text-primary-foreground" disabled={soldOut}
                onClick={() => { setOpenDetail(false); handleSignupClick(); }} data-testid={`merit-detail-buy-${project.id}`}>
                {isShop ? (soldOut ? t("shop.soldOut") : t("shop.buy")) : t("common.signup")}
              </Button>
              <Button variant="outline" size="icon" className="border-[hsl(var(--brand-gold))]" onClick={handleShare} aria-label={t("common.share")}><Share2 className="h-4 w-4" /></Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
